import {
  ChangeDetectionStrategy,
  Component,
  computed,
  Inject,
  OnInit,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { IGetAppPageForm } from "../../../../rest-clients/application/app/forms/i-get-app-page.form";
import { IPage } from '@zaeper/communication-lib';
import { Button } from "primeng/button";
import {
  createAppModalServiceInjectionToken
} from "../../../../modals/create-app-modal/create-app-modal-service-injection-token";
import { ICreateAppModalService } from "../../../../modals/create-app-modal/i-create-app-modal.service";
import { ICreateAppModalOptions } from "../../../../modals/create-app-modal/i-create-app-modal.options";
import { appServiceInjectionToken } from "../../../../services/application/app/app-service-injection-token";
import { IAppService } from "../../../../services/application/app/i-app.service";
import { IApp } from "../../../../models/application/i-app";
import {
  CardComponent,
  ConfirmationModalService,
  confirmationModalServiceInjectionToken,
  ESeverity,
  IConfirmationModalService,
  IToastService,
  toastServiceInjectionToken
} from '@zaeper/angular-dashboard-view-lib';
import { ActivatedRoute, Data, Router } from "@angular/router";
import { ApplicationOverviewItemComponent } from "../../../../components/application-overview-item";
import { ConfirmationService } from "primeng/api";
import { appRestClientInjectionToken } from "../../../../rest-clients/application/app/app-rest-client-injection-token";
import { IAppRestClient } from "../../../../rest-clients/application/app/i-app-rest.client";

@Component( {
  selector: 'app-apps-overview-tab-component',
  templateUrl: 'apps-overview-tab.component.html',
  imports: [
    Button, CardComponent, ApplicationOverviewItemComponent
  ],
  providers: [
    {
      provide: confirmationModalServiceInjectionToken,
      useClass: ConfirmationModalService
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
} )
export class AppsOverviewTabComponent implements OnInit {
  private readonly _appPage$: WritableSignal<IPage<IApp> | undefined> = signal<IPage<IApp> | undefined>( undefined );
  protected apps$: Signal<IApp[]> = computed( () => (this._appPage$()?.records) ?? [] );

  constructor(
    @Inject( createAppModalServiceInjectionToken ) private readonly _createAppModalService: ICreateAppModalService,
    @Inject( appServiceInjectionToken ) private readonly _appService: IAppService,
    @Inject( appRestClientInjectionToken ) private readonly _appRestClient: IAppRestClient,
    @Inject( toastServiceInjectionToken ) private readonly _toastService: IToastService,
    @Inject( confirmationModalServiceInjectionToken ) private readonly _confirmationModalService: IConfirmationModalService,
    private readonly _confirmService: ConfirmationService,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _router: Router
  ) {
  }

  ngOnInit() {
    this._activatedRoute.data.subscribe( ({ appPage }: Data) => {
      this._appPage$.set( appPage );
    } );
  }

  protected openCreateAppModal(): void {
    const options: ICreateAppModalOptions = {
      onConfirm: function (): void {
        throw new Error( 'Function not implemented.' );
      }
    };

    this._createAppModalService.open( options );
  }

  protected openAppDetailPage(appId: string): Promise<boolean> {
    return this._router.navigate( [ "de", "app", appId ] );
  }

  protected async deleteApp(
    event: Event,
    app: IApp
  ): Promise<void> {
    event.stopPropagation();

    const sendDeleteRequest = async () => {
      try {
        await this._appRestClient.deleteApp( app.id );

        this._toastService.setMessage( {
          title: "Erfolgreich",
          detail: "App wurde erfolgreich gelöscht.",
          severity: ESeverity.success
        } );

        await this._loadAppRecords();
      } catch ( e ) {
        this._toastService.setMessage( {
          title: "Fehler",
          detail: "App konnte nicht gelöscht werden.",
          severity: ESeverity.error
        } );
      }
    };

    this._confirmationModalService.open( {
      title: `${ app.name } löschen?`,
      description: 'Dadurch werden auch alle veröffentlichten Versionen aus dem Store entfernt.',
      confirmationText: 'Löschen',
      onConfirm: () => sendDeleteRequest()
    })
  }

  private async _loadAppRecords() {
    const getAppPageForm: IGetAppPageForm = {
      pageNumber: 0,
      pageSize: 100
    };

    const appPage: IPage<IApp> = await this._appService.getAppPage( getAppPageForm );
    this._appPage$.set( appPage );
  }
}
